import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import isHotkey from "is-hotkey";
import escapeHtml from "escape-html";
import Swal from "sweetalert2";
import {
  Editor,
  Element as SlateElement,
  Node as SlateNode,
  Path,
  Point,
  Range,
  Text,
  Transforms,
  createEditor
} from "slate";
import {
  Editable,
  Slate,
  useFocused,
  useSelected,
  useSlate,
  withReact
} from "slate-react";
import { withHistory } from "slate-history";
import { Button, Icon, Toolbar } from "views/MerchantPromo/Components2";
import { checkImageUrl } from "./AnnouncementShared";

// Block types and the HTML each one is saved as. Everything here is styled by
// the website's announcement page, so what the admin sees is what gets shown.
const BLOCKS = {
  paragraph: { tag: "p" },
  greeting: { tag: "p", className: "greeting" },
  note: { tag: "p", className: "italic" },
  "heading-two": { tag: "h2" },
  "heading-three": { tag: "h3" },
  "bulleted-list": { tag: "ul" },
  "numbered-list": { tag: "ol" },
  "list-item": { tag: "li" }
};

const LIST_TYPES = ["bulleted-list", "numbered-list"];

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline"
};

const emptyValue = [{ type: "paragraph", children: [{ text: "" }] }];

// ---------- HTML <-> editor value ----------

const SUPPORTED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "UL",
  "OL",
  "LI",
  "A",
  "H1",
  "H2",
  "H3",
  "H4",
  "SPAN",
  "TABLE",
  "THEAD",
  "TBODY",
  "TR",
  "TH",
  "TD",
  "IMG"
]);

const TAG_LABELS = {
  DIV: "layout blocks (div)",
  BLOCKQUOTE: "quotes",
  IFRAME: "embedded frames"
};

// Classes the editor keeps, per element.
const KEPT_CLASSES = {
  P: ["greeting", "italic"],
  DIV: ["table-wrapper"],
  TD: ["value"],
  TH: ["value"]
};

const isTableWrapper = el =>
  el.nodeName === "DIV" &&
  el.classList.contains("table-wrapper") &&
  el.querySelector("table") !== null;

// Lists what the visual editor would drop if this HTML were opened in it.
export const findUnsupported = html => {
  if (!html || !html.trim()) return [];
  const body = new DOMParser().parseFromString(html, "text/html").body;
  const issues = new Set();
  body.querySelectorAll("*").forEach(el => {
    if (!SUPPORTED_TAGS.has(el.nodeName) && !isTableWrapper(el)) {
      issues.add(
        TAG_LABELS[el.nodeName] ?? "<" + el.nodeName.toLowerCase() + ">"
      );
      return;
    }
    if (el.hasAttribute("colspan") || el.hasAttribute("rowspan"))
      issues.add("merged table cells");
    el.classList.forEach(cls => {
      if (!(KEPT_CLASSES[el.nodeName] ?? []).includes(cls))
        issues.add('styling class "' + cls + '"');
    });
  });
  return Array.from(issues);
};

const ofType = type => node =>
  SlateElement.isElement(node) && node.type === type;

const INLINE_TYPES = ["link", "image"];

const isBlockNode = node =>
  SlateElement.isElement(node) && !INLINE_TYPES.includes(node.type);

// Table cells hold inline content only, so blocks inside a cell are flattened.
const toInline = nodes =>
  nodes.flatMap(node => (isBlockNode(node) ? node.children : [node]));

const imageNode = ({ url, alt, width }) => ({
  type: "image",
  url,
  alt: alt || "",
  width: width || null,
  children: [{ text: "" }]
});

const deserializeNode = (el, marks) => {
  if (el.nodeType === Node.TEXT_NODE) {
    return { text: el.textContent, ...marks };
  }
  if (el.nodeType !== Node.ELEMENT_NODE) return null;

  const childMarks = { ...marks };
  if (el.nodeName === "STRONG" || el.nodeName === "B") childMarks.bold = true;
  if (el.nodeName === "EM" || el.nodeName === "I") childMarks.italic = true;
  if (el.nodeName === "U") childMarks.underline = true;

  const children = Array.from(el.childNodes)
    .map(child => deserializeNode(child, childMarks))
    .flat()
    .filter(Boolean);
  const withText = children.length ? children : [{ text: "", ...marks }];

  switch (el.nodeName) {
    case "BR":
      return { text: "\n", ...marks };
    case "P": {
      const type = el.classList.contains("greeting")
        ? "greeting"
        : el.classList.contains("italic")
        ? "note"
        : "paragraph";
      return { type, children: withText };
    }
    case "H1":
    case "H2":
      return { type: "heading-two", children: withText };
    case "H3":
    case "H4":
      return { type: "heading-three", children: withText };
    case "UL":
      return { type: "bulleted-list", children: children.filter(isBlockNode) };
    case "OL":
      return { type: "numbered-list", children: children.filter(isBlockNode) };
    case "LI":
      return { type: "list-item", children: withText };
    case "A":
      return {
        type: "link",
        url: el.getAttribute("href") ?? "",
        children: withText
      };
    case "IMG":
      return el.getAttribute("src")
        ? imageNode({
            url: el.getAttribute("src"),
            alt: el.getAttribute("alt"),
            width: el.getAttribute("width")
          })
        : null;
    case "TABLE": {
      const rows = children.filter(ofType("table-row"));
      return rows.length ? { type: "table", children: rows } : null;
    }
    case "THEAD":
    case "TBODY":
      return children.filter(ofType("table-row"));
    case "TR": {
      const cells = children.filter(ofType("table-cell"));
      return cells.length ? { type: "table-row", children: cells } : null;
    }
    case "TH":
    case "TD": {
      const inline = toInline(children);
      return {
        type: "table-cell",
        header: el.nodeName === "TH",
        value: el.classList.contains("value"),
        children: inline.length ? inline : [{ text: "" }]
      };
    }
    default:
      return children;
  }
};

export const htmlToValue = html => {
  if (!html || !html.trim()) return emptyValue;
  const body = new DOMParser().parseFromString(html, "text/html").body;

  // Top level must be blocks: wrap loose text and links into paragraphs.
  const blocks = [];
  let loose = [];
  const flushLoose = () => {
    if (loose.some(n => !Text.isText(n) || n.text.trim()))
      blocks.push({ type: "paragraph", children: loose });
    loose = [];
  };
  Array.from(body.childNodes)
    .map(node => deserializeNode(node, {}))
    .flat()
    .filter(Boolean)
    .forEach(node => {
      if (isBlockNode(node)) {
        flushLoose();
        blocks.push(node);
      } else {
        loose.push(node);
      }
    });
  flushLoose();
  return blocks.length ? blocks : emptyValue;
};

const serializeNode = node => {
  if (Text.isText(node)) {
    let html = escapeHtml(node.text).replace(/\n/g, "<br>");
    if (node.underline) html = `<u>${html}</u>`;
    if (node.italic) html = `<em>${html}</em>`;
    if (node.bold) html = `<strong>${html}</strong>`;
    return html;
  }

  if (node.type === "table") return serializeTable(node);
  if (node.type === "image") {
    const widthAttr = node.width ? ` width="${escapeHtml(node.width)}"` : "";
    return `<img src="${escapeHtml(node.url)}" alt="${escapeHtml(
      node.alt ?? ""
    )}"${widthAttr}>`;
  }

  const children = node.children.map(serializeNode).join("");
  if (node.type === "link")
    return children ? `<a href="${escapeHtml(node.url)}">${children}</a>` : "";
  if (node.type === "table-cell") {
    const tag = node.header ? "th" : "td";
    const classAttr = node.value ? ' class="value"' : "";
    return `<${tag}${classAttr}>${children}</${tag}>`;
  }

  const block = BLOCKS[node.type] ?? BLOCKS.paragraph;
  const classAttr = block.className ? ` class="${block.className}"` : "";
  return `<${block.tag}${classAttr}>${children}</${block.tag}>`;
};

// Leading rows made only of header cells go into <thead>, the rest into <tbody>.
const serializeTable = table => {
  const rows = table.children;
  let headCount = 0;
  while (
    headCount < rows.length &&
    rows[headCount].children.every(cell => cell.header)
  )
    headCount++;

  const row = r => `<tr>${r.children.map(serializeNode).join("")}</tr>`;
  const head = rows
    .slice(0, headCount)
    .map(row)
    .join("\n");
  const body = rows
    .slice(headCount)
    .map(row)
    .join("\n");
  return (
    '<div class="table-wrapper"><table>' +
    (head ? `\n<thead>${head}</thead>` : "") +
    (body ? `\n<tbody>\n${body}\n</tbody>` : "") +
    "\n</table></div>"
  );
};

const TEXT_BLOCKS = [
  "paragraph",
  "greeting",
  "note",
  "heading-two",
  "heading-three"
];

// Empty text blocks (e.g. the spare line after a table) are not saved.
// A line holding only an image or link is not empty.
const isBlankBlock = node =>
  TEXT_BLOCKS.includes(node.type) &&
  SlateNode.string(node).trim() === "" &&
  !node.children.some(child => SlateElement.isElement(child));

export const valueToHtml = value =>
  value
    .filter(node => !isBlankBlock(node))
    .map(serializeNode)
    .join("\n");

// ---------- Editor ----------

const withInlines = editor => {
  const { isInline, isVoid } = editor;
  editor.isInline = element =>
    INLINE_TYPES.includes(element.type) ? true : isInline(element);
  editor.isVoid = element =>
    element.type === "image" ? true : isVoid(element);
  return editor;
};

const isValidImageUrl = url => /^https?:\/\/\S+$/i.test(url);

// Resolves to { url, alt, width } or null when cancelled.
const promptImage = (initial = {}) =>
  Swal.fire({
    title: initial.url ? "Edit image" : "Insert image",
    html:
      '<input id="announcement-img-url" class="swal2-input" placeholder="Image URL (https://..., .jpg, max 2000 x 2000 px)">' +
      '<input id="announcement-img-alt" class="swal2-input" placeholder="Description (alt text)">' +
      '<input id="announcement-img-width" class="swal2-input" type="number" min="1" placeholder="Width in px (optional)">',
    showCancelButton: true,
    focusConfirm: false,
    confirmButtonText: initial.url ? "Save" : "Insert",
    didOpen: () => {
      document.getElementById("announcement-img-url").value = initial.url ?? "";
      document.getElementById("announcement-img-alt").value = initial.alt ?? "";
      document.getElementById("announcement-img-width").value =
        initial.width ?? "";
    },
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    preConfirm: async () => {
      const url = document.getElementById("announcement-img-url").value.trim();
      const alt = document.getElementById("announcement-img-alt").value.trim();
      const width = document
        .getElementById("announcement-img-width")
        .value.trim();
      if (!isValidImageUrl(url)) {
        Swal.showValidationMessage("Enter an image URL starting with https://");
        return false;
      }
      if (width && !/^[1-9]\d*$/.test(width)) {
        Swal.showValidationMessage("Width must be a whole number of pixels");
        return false;
      }
      const problem = await checkImageUrl(url);
      if (problem) {
        Swal.showValidationMessage(problem);
        return false;
      }
      return { url, alt, width };
    }
  }).then(result => (result.isConfirmed ? result.value : null));

const insertImage = async editor => {
  // The dialog takes focus, so remember where the caret was.
  const at = editor.selection;
  const image = await promptImage();
  if (!image) return;
  if (at) Transforms.select(editor, at);
  else Transforms.select(editor, Editor.end(editor, []));
  Transforms.insertNodes(editor, imageNode(image));
};

const findSelectedImage = editor => {
  if (!editor.selection) return undefined;
  const [entry] = Array.from(Editor.nodes(editor, { match: ofType("image") }));
  return entry;
};

const imageAction = async (editor, action) => {
  const entry = findSelectedImage(editor);
  if (!entry) return;
  const [image, path] = entry;
  const parent = SlateNode.parent(editor, path);

  switch (action) {
    case "edit": {
      const updated = await promptImage(image);
      if (updated)
        Transforms.setNodes(
          editor,
          { url: updated.url, alt: updated.alt, width: updated.width || null },
          { at: path }
        );
      break;
    }
    case "link":
      if (parent.type === "link") {
        Transforms.unwrapNodes(editor, { at: Path.parent(path) });
      } else {
        const url = window.prompt(
          "Link URL for this image (https://..., mailto:... or tel:...)"
        );
        if (url)
          Transforms.wrapNodes(
            editor,
            { type: "link", url, children: [] },
            { at: path }
          );
      }
      break;
    case "remove": {
      // A link that only wraps this image goes with it.
      const onlyChild =
        parent.type === "link" &&
        SlateNode.string(parent) === "" &&
        parent.children.filter(child => SlateElement.isElement(child))
          .length === 1;
      Transforms.removeNodes(editor, {
        at: onlyChild ? Path.parent(path) : path
      });
      break;
    }
  }
};

const findAbove = (editor, type) =>
  editor.selection ? Editor.above(editor, { match: ofType(type) }) : undefined;

const topBlockPath = editor => {
  const entry = editor.selection
    ? Editor.above(editor, {
        match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
        mode: "highest"
      })
    : undefined;
  return entry ? entry[1] : null;
};

const emptyParagraph = () => ({ type: "paragraph", children: [{ text: "" }] });

// Keeps typing inside a table from breaking its structure.
const withTables = editor => {
  const { deleteBackward, deleteForward, insertBreak, normalizeNode } = editor;

  const caretAt = edge => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) return null;
    const cell = findAbove(editor, "table-cell");
    if (cell) {
      const point =
        edge === "start"
          ? Editor.start(editor, cell[1])
          : Editor.end(editor, cell[1]);
      return Point.equals(selection.anchor, point) ? "cell" : null;
    }
    // At the edge of a block that touches a table: merging would pull text into a cell.
    const path = topBlockPath(editor);
    if (!path) return null;
    const point =
      edge === "start" ? Editor.start(editor, path) : Editor.end(editor, path);
    const neighbour = editor.children[path[0] + (edge === "start" ? -1 : 1)];
    return Point.equals(selection.anchor, point) && neighbour?.type === "table"
      ? "beside-table"
      : null;
  };

  editor.deleteBackward = unit => {
    if (!caretAt("start")) deleteBackward(unit);
  };
  editor.deleteForward = unit => {
    if (!caretAt("end")) deleteForward(unit);
  };
  editor.insertBreak = () => {
    if (findAbove(editor, "table-cell")) editor.insertText("\n");
    else insertBreak();
  };
  // Always leave a line after a trailing table so the admin can keep typing.
  editor.normalizeNode = entry => {
    const [node] = entry;
    if (Editor.isEditor(node)) {
      const last = node.children[node.children.length - 1];
      if (last?.type === "table") {
        Transforms.insertNodes(editor, emptyParagraph(), {
          at: [node.children.length]
        });
        return;
      }
    }
    normalizeNode(entry);
  };
  return editor;
};

const newCell = header => ({
  type: "table-cell",
  header,
  value: false,
  children: [{ text: "" }]
});

const newRow = (columns, header) => ({
  type: "table-row",
  children: Array.from({ length: columns }, () => newCell(header))
});

const insertTable = editor => {
  const path = topBlockPath(editor);
  if (!path || findAbove(editor, "table")) return;

  const table = {
    type: "table",
    children: [newRow(2, true), newRow(2, false), newRow(2, false)]
  };
  // On an empty line the table goes above it and that line stays as the one after;
  // otherwise the table and a fresh line go below the current block.
  const onEmptyLine = isBlankBlock(editor.children[path[0]]);
  const at = onEmptyLine ? path : Path.next(path);
  Transforms.insertNodes(
    editor,
    onEmptyLine ? table : [table, emptyParagraph()],
    { at }
  );
  Transforms.select(editor, Editor.start(editor, at));
};

const tableAction = (editor, action) => {
  const table = findAbove(editor, "table");
  const row = findAbove(editor, "table-row");
  const cell = findAbove(editor, "table-cell");
  if (!table || !row || !cell) return;

  const [tableNode, tablePath] = table;
  const [rowNode, rowPath] = row;
  const [cellNode, cellPath] = cell;
  const column = cellPath[cellPath.length - 1];
  const removeTable = () => {
    Transforms.removeNodes(editor, { at: tablePath });
    if (editor.children.length === 0)
      Transforms.insertNodes(editor, emptyParagraph(), { at: [0] });
  };

  Editor.withoutNormalizing(editor, () => {
    switch (action) {
      case "add-row":
        Transforms.insertNodes(editor, newRow(rowNode.children.length, false), {
          at: Path.next(rowPath)
        });
        break;
      case "add-column":
        tableNode.children.forEach((r, index) => {
          const header = r.children.every(c => c.header);
          Transforms.insertNodes(editor, newCell(header), {
            at: [...tablePath, index, Math.min(column + 1, r.children.length)]
          });
        });
        break;
      case "remove-row":
        if (tableNode.children.length === 1) removeTable();
        else Transforms.removeNodes(editor, { at: rowPath });
        break;
      case "remove-column":
        if (rowNode.children.length === 1) {
          removeTable();
          break;
        }
        // Remove from the bottom up so earlier paths stay valid.
        for (let index = tableNode.children.length - 1; index >= 0; index--) {
          if (tableNode.children[index].children.length > column)
            Transforms.removeNodes(editor, {
              at: [...tablePath, index, column]
            });
        }
        break;
      case "header-row": {
        const header = !rowNode.children.every(c => c.header);
        rowNode.children.forEach((_, index) =>
          Transforms.setNodes(editor, { header }, { at: [...rowPath, index] })
        );
        break;
      }
      case "value":
        Transforms.setNodes(
          editor,
          { value: !cellNode.value },
          { at: cellPath }
        );
        break;
      case "delete-table":
        removeTable();
        break;
    }
  });
};

const isBlockActive = (editor, format) => {
  const [match] = Array.from(
    Editor.nodes(editor, {
      match: n =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format
    })
  );
  return !!match;
};

const toggleBlock = (editor, format) => {
  // Block styles don't apply inside table cells.
  if (findAbove(editor, "table-cell")) return;
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type),
    split: true
  });
  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : isList ? "list-item" : format
  });
  if (!isActive && isList) {
    Transforms.wrapNodes(editor, { type: format, children: [] });
  }
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor, format) => {
  if (isMarkActive(editor, format)) Editor.removeMark(editor, format);
  else Editor.addMark(editor, format, true);
};

const isLinkActive = editor => isBlockActive(editor, "link");

const toggleLink = editor => {
  if (isLinkActive(editor)) {
    Transforms.unwrapNodes(editor, {
      match: n =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link"
    });
    return;
  }
  const url = window.prompt("Link URL (https://..., mailto:... or tel:...)");
  if (!url) return;

  const { selection } = editor;
  if (selection && Range.isCollapsed(selection)) {
    Transforms.insertNodes(editor, {
      type: "link",
      url,
      children: [{ text: url }]
    });
  } else {
    Transforms.wrapNodes(
      editor,
      { type: "link", url, children: [] },
      { split: true }
    );
    Transforms.collapse(editor, { edge: "end" });
  }
};

const ToolbarButton = ({ active, onToggle, icon, label }) => (
  <Button
    active={active}
    title={label}
    onMouseDown={event => {
      event.preventDefault();
      onToggle();
    }}
  >
    {icon ? <Icon>{icon}</Icon> : <b style={{ fontSize: 13 }}>{label}</b>}
  </Button>
);

ToolbarButton.propTypes = {
  active: PropTypes.bool,
  onToggle: PropTypes.func,
  icon: PropTypes.string,
  label: PropTypes.string
};

const MarkButton = ({ format, icon, label }) => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={isMarkActive(editor, format)}
      onToggle={() => toggleMark(editor, format)}
      icon={icon}
      label={label}
    />
  );
};

MarkButton.propTypes = {
  format: PropTypes.string,
  icon: PropTypes.string,
  label: PropTypes.string
};

const BlockButton = ({ format, icon, label }) => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={isBlockActive(editor, format)}
      onToggle={() => toggleBlock(editor, format)}
      icon={icon}
      label={label}
    />
  );
};

BlockButton.propTypes = {
  format: PropTypes.string,
  icon: PropTypes.string,
  label: PropTypes.string
};

const LinkButton = () => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={isLinkActive(editor)}
      onToggle={() => toggleLink(editor)}
      icon="link"
      label="Link"
    />
  );
};

const TableButton = () => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={!!findAbove(editor, "table")}
      onToggle={() => insertTable(editor)}
      icon="table_chart"
      label="Insert table"
    />
  );
};

const ImageButton = () => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={!!findSelectedImage(editor)}
      onToggle={() => insertImage(editor)}
      icon="image"
      label="Insert image"
    />
  );
};

// Shown only while an image is selected.
const ImageToolbar = () => {
  const editor = useSlate();
  const entry = findSelectedImage(editor);
  if (!entry) return null;
  const isLinked = SlateNode.parent(editor, entry[1]).type === "link";

  return (
    <Toolbar>
      <span style={{ fontSize: 12, color: "#888" }}>Image:</span>
      <ToolbarButton
        onToggle={() => imageAction(editor, "edit")}
        label="Edit"
      />
      <ToolbarButton
        active={isLinked}
        onToggle={() => imageAction(editor, "link")}
        label={isLinked ? "Unlink" : "Link"}
      />
      <ToolbarButton
        onToggle={() => imageAction(editor, "remove")}
        label="Remove"
      />
    </Toolbar>
  );
};

const ImageElement = ({ attributes, children, element }) => {
  const selected = useSelected();
  const focused = useFocused();
  return (
    <span {...attributes}>
      <span
        contentEditable={false}
        style={{ display: "inline-block", verticalAlign: "bottom" }}
      >
        <img
          src={element.url}
          alt={element.alt}
          width={element.width || undefined}
          style={{
            display: "block",
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
            boxShadow: selected && focused ? "0 0 0 3px #B4D5FF" : "none"
          }}
        />
      </span>
      {children}
    </span>
  );
};

ImageElement.propTypes = {
  attributes: PropTypes.any,
  children: PropTypes.any,
  element: PropTypes.any
};

const TABLE_ACTIONS = [
  { action: "add-row", label: "+ Row" },
  { action: "add-column", label: "+ Column" },
  { action: "remove-row", label: "− Row" },
  { action: "remove-column", label: "− Column" },
  { action: "header-row", label: "Header row" },
  { action: "value", label: "Value" },
  { action: "delete-table", label: "Delete table" }
];

// Shown only while the caret is inside a table.
const TableToolbar = () => {
  const editor = useSlate();
  const row = findAbove(editor, "table-row");
  const cell = findAbove(editor, "table-cell");
  if (!row || !cell) return null;

  const active = {
    "header-row": row[0].children.every(c => c.header),
    value: !!cell[0].value
  };
  return (
    <Toolbar>
      <span style={{ fontSize: 12, color: "#888" }}>Table:</span>
      {TABLE_ACTIONS.map(({ action, label }) => (
        <ToolbarButton
          key={action}
          active={!!active[action]}
          onToggle={() => tableAction(editor, action)}
          label={label}
        />
      ))}
    </Toolbar>
  );
};

const Element = ({ attributes, children, element }) => {
  switch (element.type) {
    case "image":
      return (
        <ImageElement attributes={attributes} element={element}>
          {children}
        </ImageElement>
      );
    case "table":
      return (
        <table
          {...attributes}
          style={{
            width: "100%",
            borderCollapse: "collapse",
            margin: "12px 0"
          }}
        >
          <tbody>{children}</tbody>
        </table>
      );
    case "table-row":
      return <tr {...attributes}>{children}</tr>;
    case "table-cell": {
      const Tag = element.header ? "th" : "td";
      return (
        <Tag
          {...attributes}
          style={{
            border: "1px solid #e0e0e0",
            padding: "8px 12px",
            textAlign: "left",
            minWidth: 80,
            background: element.header ? "#0169AF" : undefined,
            color: element.header
              ? "#fff"
              : element.value
              ? "#0169AF"
              : undefined,
            fontWeight: element.header || element.value ? "bold" : undefined
          }}
        >
          {children}
        </Tag>
      );
    }
    case "greeting":
      return (
        <p {...attributes} style={{ color: "#054f99", fontWeight: "bold" }}>
          {children}
        </p>
      );
    case "note":
      return (
        <p {...attributes} style={{ fontStyle: "italic", color: "#555" }}>
          {children}
        </p>
      );
    case "heading-two":
      return (
        <h2 {...attributes} style={{ color: "#054f99", fontSize: 22 }}>
          {children}
        </h2>
      );
    case "heading-three":
      return (
        <h3 {...attributes} style={{ color: "#054f99", fontSize: 18 }}>
          {children}
        </h3>
      );
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;
    case "list-item":
      return <li {...attributes}>{children}</li>;
    case "link":
      return (
        <a {...attributes} href={element.url} style={{ color: "#0169AF" }}>
          {children}
        </a>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

Element.propTypes = {
  attributes: PropTypes.any,
  children: PropTypes.any,
  element: PropTypes.any
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

Leaf.propTypes = {
  attributes: PropTypes.any,
  children: PropTypes.any,
  leaf: PropTypes.any
};

// Converts the given HTML once on mount; remount (change `key`) to load new HTML.
const AnnouncementRichText = ({ html, onChange }) => {
  const editor = useMemo(
    () => withTables(withInlines(withHistory(withReact(createEditor())))),
    []
  );
  const initialValue = useMemo(() => htmlToValue(html), []);
  const renderElement = useCallback(props => <Element {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  const handleChange = value => {
    // Selection changes also fire onChange; only real edits rewrite the HTML.
    if (editor.operations.every(op => op.type === "set_selection")) return;
    onChange(valueToHtml(value));
  };

  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 4, padding: 12 }}>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onChange={handleChange}
      >
        <Toolbar>
          <MarkButton format="bold" icon="format_bold" label="Bold" />
          <MarkButton format="italic" icon="format_italic" label="Italic" />
          <MarkButton
            format="underline"
            icon="format_underlined"
            label="Underline"
          />
          <LinkButton />
          <BlockButton format="greeting" label="Greeting" />
          <BlockButton format="heading-two" label="H2" />
          <BlockButton format="heading-three" label="H3" />
          <BlockButton format="note" label="Note" />
          <BlockButton
            format="bulleted-list"
            icon="format_list_bulleted"
            label="Bulleted list"
          />
          <BlockButton
            format="numbered-list"
            icon="format_list_numbered"
            label="Numbered list"
          />
          <TableButton />
          <ImageButton />
        </Toolbar>
        <TableToolbar />
        <ImageToolbar />
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder="Dear Valued Passengers, ..."
          spellCheck
          style={{ minHeight: 260, lineHeight: 1.6 }}
          onKeyDown={event => {
            for (const hotkey in HOTKEYS) {
              if (isHotkey(hotkey, event)) {
                event.preventDefault();
                toggleMark(editor, HOTKEYS[hotkey]);
              }
            }
          }}
        />
      </Slate>
    </div>
  );
};

export default AnnouncementRichText;

AnnouncementRichText.propTypes = {
  html: PropTypes.string,
  onChange: PropTypes.func
};
